/**
 * CDL Consultants requests the sweep could not place on a case (Lloyd, 14 Sep 2026).
 *
 * The sweep matches their email to a case by the subject line, which is where they put the driver's
 * name and the ticket number. Measured on the live mailbox that day, **18 of 25 matched nothing**,
 * and the old behaviour was to count those and drop them — so the same request was re-read and
 * re-dropped on every run, with no record it had ever been seen.
 *
 * They are queued now, and this is where a person clears them. Without this screen the table fills
 * up and nobody reads it, which is the same failure in a different place.
 *
 * ## Oldest first, deliberately
 *
 * The oldest is the partner who has waited longest, and Lloyd's own complaint about CDL was
 * *"I keep getting these annoying emails"* — which is what happens when the first one goes
 * unanswered.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Nav from '../../components/Nav/Nav';

interface CdlRequestDto {
  UnmatchedCdlRequest_guid: string;
  UnmatchedCdlRequest_Subject: string | null;
  UnmatchedCdlRequest_From: string | null;
  UnmatchedCdlRequest_ReceivedAt: string | null;
  UnmatchedCdlRequest_Kind: string;
  UnmatchedCdlRequest_ParsedName: string | null;
  UnmatchedCdlRequest_ParsedTicketNumber: string | null;
}

const KIND_LABEL: Record<string, string> = {
  new_case: 'New case',
  update: 'Update requested',
  thread: 'Reply',
};

const ManagerCdlRequestsView: React.FC = () => {
  const [requests, setRequests] = useState<CdlRequestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const host = process.env.API_HOST || '';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${host}/api/v1/manager/unmatched-cdl-requests`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to load the CDL requests');
      setRequests(await response.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the CDL requests');
    } finally {
      setLoading(false);
    }
  }, [host]);

  useEffect(() => {
    void load();
  }, [load]);

  const dismiss = async (guid: string) => {
    setBusy(guid);
    setError(null);
    try {
      const response = await fetch(`${host}/api/v1/manager/unmatched-cdl-requests/${guid}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'dismiss' }),
      });
      if (!response.ok) throw new Error('Failed to clear that request');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear that request');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Nav />
      <Container sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h4" gutterBottom>
          CDL requests needing a case
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Emails from CDL Consultants the system could not match to a case. Oldest first, because
          that is the one they have waited longest for. Clearing a request does not delete it.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : requests.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="body1" color="text.secondary">
              Nothing waiting. Either every request matched a case, or the sweep is switched off.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Received</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.UnmatchedCdlRequest_guid} hover>
                    <TableCell>
                      {request.UnmatchedCdlRequest_ReceivedAt
                        ? new Date(request.UnmatchedCdlRequest_ReceivedAt).toLocaleDateString('en-US')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          KIND_LABEL[request.UnmatchedCdlRequest_Kind] ??
                          request.UnmatchedCdlRequest_Kind
                        }
                      />
                    </TableCell>
                    {/* What the subject said, kept whether or not a case was found. Without it a
                        person has to open Outlook to know who the request is about. */}
                    <TableCell>{request.UnmatchedCdlRequest_ParsedName || '—'}</TableCell>
                    <TableCell>{request.UnmatchedCdlRequest_ParsedTicketNumber || '—'}</TableCell>
                    <TableCell>{request.UnmatchedCdlRequest_Subject || '(no subject)'}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          disabled={busy === request.UnmatchedCdlRequest_guid}
                          onClick={() => void dismiss(request.UnmatchedCdlRequest_guid)}
                        >
                          Clear
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </>
  );
};

export default ManagerCdlRequestsView;
